/**
 * Class weapon artwork (D42, 2026-10-09) — the four Weapon Training weapons
 * lane AQ added (plan 24 D33), redrawn to read at game scale.
 *
 * The first pass reused the generic family shapes: at game zoom the Stormroot
 * Totem and the Thornwood Crook read as thin sticks and the druid's crook
 * looked close to the warlock's wand. Each now has its own variant (authored
 * in content/items/equipment.toml `[<item>.visual]`) with a distinct
 * silhouette:
 *  - Vesper Staff (priest, staff 10): pale ivory shaft, gold bands, a gold
 *    crescent cradling a dusk-light orb.
 *  - Thornwood Crook (druid, staff 11): a thick gnarled shaft ending in a wide
 *    shepherd's hook, leaves along the hook — the only hooked silhouette.
 *  - Stormroot Totem (shaman, totem 10): a stout haft under a broad carved
 *    head with a face, a lightning glyph and two feathers.
 *  - Cinder Rod (warlock, wand 10): a short, straight black rod with an ember
 *    shard and flame at its tip — straight and dark where the crook is curved
 *    and green.
 *
 * Shapes are in the weapon's local space (grip at about (5, 8); up is -y),
 * the same space as WeaponParts.
 */

export const CLASS_WEAPON_VARIANT = {
  vesperStaff: 10,
  thornwoodCrook: 11,
  stormrootTotem: 10,
  cinderRod: 10,
} as const;

const OUTLINE = '#2a1d12';

function vesperStaff(primary: string, accent: string): string {
  return `
    <rect x="1.5" y="-40" width="7" height="62" rx="2" fill="${primary}" stroke="${OUTLINE}" stroke-width="1.2" />
    <rect x="0.5" y="-8" width="9" height="4" rx="1" fill="${accent}" stroke="${OUTLINE}" stroke-width="0.8" />
    <rect x="0.5" y="12" width="9" height="4" rx="1" fill="${accent}" stroke="${OUTLINE}" stroke-width="0.8" />
    <path d="M-9,-48 Q-10,-62 5,-66 Q-3,-58 -2,-48 Q-1,-40 5,-38 Q-6,-36 -9,-48 Z" fill="${accent}" stroke="${OUTLINE}" stroke-width="1.2" />
    <path d="M19,-48 Q20,-62 5,-66 Q13,-58 12,-48 Q11,-40 5,-38 Q16,-36 19,-48 Z" fill="${accent}" stroke="${OUTLINE}" stroke-width="1.2" />
    <circle cx="5" cy="-50" r="7" fill="#fff6d8" stroke="${accent}" stroke-width="2" />
    <circle cx="5" cy="-50" r="11" fill="${accent}" opacity="0.25" />`;
}

function thornwoodCrook(primary: string, accent: string): string {
  // Thick wooden shaft, then a wide hook curling back over the hand.
  return `
    <path d="M5,22 Q2,4 6,-14 Q9,-30 5,-44 Q2,-58 12,-64 Q24,-68 27,-56 Q29,-46 21,-42"
      fill="none" stroke="${OUTLINE}" stroke-width="10" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M5,22 Q2,4 6,-14 Q9,-30 5,-44 Q2,-58 12,-64 Q24,-68 27,-56 Q29,-46 21,-42"
      fill="none" stroke="${primary}" stroke-width="7" stroke-linecap="round" stroke-linejoin="round" />
    <path d="M4,4 l-2,-2 M7,-22 l3,-2 M4,-36 l-3,-1" stroke="${OUTLINE}" stroke-width="1.4" stroke-linecap="round" />
    <path d="M14,-66 Q8,-76 16,-79 Q20,-72 14,-66 Z" fill="${accent}" stroke="${OUTLINE}" stroke-width="1" />
    <path d="M24,-63 Q30,-72 35,-66 Q30,-60 24,-63 Z" fill="${accent}" stroke="${OUTLINE}" stroke-width="1" />
    <path d="M27,-50 Q35,-50 35,-42 Q28,-42 27,-50 Z" fill="${accent}" stroke="${OUTLINE}" stroke-width="1" />
    <path d="M6,-40 Q-2,-44 -4,-36 Q3,-34 6,-40 Z" fill="${accent}" stroke="${OUTLINE}" stroke-width="1" />`;
}

function stormrootTotem(primary: string, accent: string): string {
  return `
    <rect x="1" y="-14" width="8" height="34" rx="2" fill="${primary}" stroke="${OUTLINE}" stroke-width="1.2" />
    <rect x="0" y="4" width="10" height="3" fill="${accent}" opacity="0.85" />
    <path d="M-6,-44 L16,-44 L18,-16 L-8,-16 Z" fill="${primary}" stroke="${OUTLINE}" stroke-width="1.4" stroke-linejoin="round" />
    <path d="M-6,-44 L0,-50 L10,-50 L16,-44 Z" fill="${primary}" stroke="${OUTLINE}" stroke-width="1.2" stroke-linejoin="round" />
    <rect x="-3" y="-38" width="5" height="4" fill="${accent}" />
    <rect x="8" y="-38" width="5" height="4" fill="${accent}" />
    <path d="M-2,-27 L12,-27 L10,-22 L0,-22 Z" fill="${OUTLINE}" />
    <path d="M6,-33 L2,-27 L6,-27 L3,-20" fill="none" stroke="${accent}" stroke-width="1.6" stroke-linejoin="round" />
    <path d="M-8,-40 Q-18,-36 -16,-24 Q-11,-30 -8,-32 Z" fill="#f2efe6" stroke="${OUTLINE}" stroke-width="1" />
    <path d="M18,-40 Q28,-36 26,-24 Q21,-30 18,-32 Z" fill="#f2efe6" stroke="${OUTLINE}" stroke-width="1" />
    <path d="M-16,-24 l2,-4 M26,-24 l-2,-4" stroke="${accent}" stroke-width="1.6" stroke-linecap="round" />`;
}

function cinderRod(primary: string, accent: string): string {
  return `
    <rect x="2.5" y="-26" width="5" height="44" rx="1.5" fill="${primary}" stroke="#000" stroke-width="1" />
    <rect x="1.5" y="10" width="7" height="3" fill="${accent}" opacity="0.8" />
    <path d="M0,-26 L10,-26 L8,-30 L2,-30 Z" fill="#5a3a30" stroke="#000" stroke-width="0.8" />
    <path d="M5,-46 L10,-34 L5,-28 L0,-34 Z" fill="${accent}" stroke="#000" stroke-width="1" />
    <path d="M5,-44 L7.5,-35 L5,-31 L2.5,-35 Z" fill="#ffb27a" />
    <path d="M5,-46 Q1,-52 5,-58 Q4,-52 8,-50 Q9,-55 7,-58 Q13,-52 8,-45 Z" fill="#ff7a3a" opacity="0.85" />`;
}

/**
 * Artwork for a class weapon variant, or null when (family, variant) is not
 * one of them (the caller then draws its generic family shape).
 */
export function renderClassWeaponArtwork(
  family: string,
  variant: number,
  primary: string,
  accent: string,
): string | null {
  if (family === 'staff' && variant === CLASS_WEAPON_VARIANT.vesperStaff) return vesperStaff(primary, accent);
  if (family === 'staff' && variant === CLASS_WEAPON_VARIANT.thornwoodCrook) return thornwoodCrook(primary, accent);
  if (family === 'totem' && variant === CLASS_WEAPON_VARIANT.stormrootTotem) return stormrootTotem(primary, accent);
  if (family === 'wand' && variant === CLASS_WEAPON_VARIANT.cinderRod) return cinderRod(primary, accent);
  return null;
}
